import React, { useEffect, useCallback, useMemo } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';



const initialState = {
  isLocked: false,
  isPrintView: false,
  helpTopics: ['default'],
  helpVars: ['default'],
  isHelpSidebarVisible: false,
  theme: 'light' as ThemePreference,
  programColor : 'bg-gray-50'
};

const themeStorageKey = 'ui-theme';

const isThemePreference = (value: string | null): value is ThemePreference => (
  value === 'light' || value === 'dark' || value === 'system'
);

const readStoredTheme = (): ThemePreference => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const stored = window.localStorage.getItem(themeStorageKey);
  return isThemePreference(stored) ? stored : 'light';
};

export const StoreContext = React.createContext<any>(undefined);

export const StoreProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = React.useState(initialState);

  useEffect(() => {
    const storedTheme = readStoredTheme();
    setState((prev: any) => prev.theme === storedTheme ? prev : { ...prev, theme: storedTheme });
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const root = window.document.documentElement;

    const applyTheme = () => {
      const resolvedTheme = state.theme === 'system' && mediaQuery.matches ? 'dark' : state.theme === 'dark' ? 'dark' : 'light';
      root.classList.remove('light', 'dark');
      root.classList.add(resolvedTheme);
    };

    applyTheme();
    mediaQuery.addEventListener('change', applyTheme);
    return () => mediaQuery.removeEventListener('change', applyTheme);
  }, [state.theme]);

  const lock = useCallback(() => setState((prev:any) => ({ ...prev, isLocked: true })), []);
  const unlock = useCallback(() => setState((prev:any) => ({ ...prev, isLocked: false })), []);
  const setPrintView = useCallback((printView: boolean) => setState((prev:any) => ({ ...prev, isPrintView: printView })), []);
  const pushHelpTopic = useCallback((topic: string, vars: any = null) => setState((prev:any) => ({
    ...prev,
    helpTopics: [...prev.helpTopics, topic],
    helpVars: [...prev.helpVars, vars]
  })), []);
  const popHelpTopic = useCallback(() => setState((prev:any) => ({
    ...prev,
    helpTopics: prev.helpTopics.slice(0, -1),
    helpVars: prev.helpVars.slice(0, -1)
  })), []);
  const showHelpSidebar = useCallback(() => setState((prev:any) => ({ ...prev, isHelpSidebarVisible: true })), []);
  const hideHelpSidebar = useCallback(() => setState((prev:any) => ({ ...prev, isHelpSidebarVisible: false })), []);
  const setProgramColor = useCallback((color: string) => setState((prev:any) => ({ ...prev, programColor: color })), []);
  const setTheme = useCallback((theme: ThemePreference) => {
    localStorage.setItem(themeStorageKey, theme);
    setState((prev: any) => {
      if (prev.theme === theme) {
        return prev;
      }
      return { ...prev, theme };
    });
  }, []);

  const currentHelpEnv = useCallback(() => ({
    topic: state.helpTopics[state.helpTopics.length - 1] || 'default',
    vars: state.helpVars[state.helpVars.length - 1] || null
  }), [state.helpTopics, state.helpVars]);

  const currentHelpTopic = useCallback(
    () => state.helpTopics[state.helpTopics.length - 1] || 'default',
    [state.helpTopics]
  );

  const store = useMemo(() => ({
    ...state,
    lock,
    unlock,
    setPrintView,
    currentHelpEnv,
    currentHelpTopic,
    pushHelpTopic,
    popHelpTopic,
    showHelpSidebar,
    hideHelpSidebar,
    setProgramColor,
    setTheme,
  }), [
    state,
    lock,
    unlock,
    setPrintView,
    currentHelpEnv,
    currentHelpTopic,
    pushHelpTopic,
    popHelpTopic,
    showHelpSidebar,
    hideHelpSidebar,
    setProgramColor,
    setTheme,
  ]);

  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
};
